import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  IconButton,
  Tooltip,
} from '@mui/material';

import {
  Business as BusinessIcon,
  People as PeopleIcon,
  Science as ScienceIcon,
  Tune as TuneIcon,
  Inventory as InventoryIcon,
  Description as DescriptionIcon,
  Verified as VerifiedIcon,
  PersonPinCircleOutlined as PersonPinCircleOutlined,
  Close as CloseIcon,
  Send as SendIcon,
  Dashboard as DashboardIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Architecture as ArchitectureIcon,
  Assignment as AssignmentIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
  AccountBalanceWallet as AccountBalanceWalletIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const Sidebar = ({ 
  isMobile, 
  onClose, 
  autoSaveProgress = 0,
  activeStep = 0,
  onSectionClick = () => {},
  showDeclaration = false, // New prop to control Declaration visibility
  collapsed = false, // New prop to control collapsed state
  onCollapseToggle = () => {} // New prop to handle collapse toggle
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [applicationStage, setApplicationStage] = useState(null);
  const [userTypeId, setUserTypeId] = useState(null);

  // Get application stage from API
  const getApplicationStage = async (appId) => {
    try {
      if (!appId) return;
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/laboratory/application-stage/${appId}`);
      const result = await response.json();
      
      if (result.success) {
        setApplicationStage(result.stage);
      } else {
        setApplicationStage('1'); // Default to 1
      }
    } catch (error) {
      console.error('Error fetching application stage:', error);
      setApplicationStage('1'); // Default to 1
    }
  };

  // Get application stage and user type
  useEffect(() => {
    const appId = localStorage.getItem('appId') || localStorage.getItem('orgId');
    const userType = localStorage.getItem('userTypeId') || '1';
    
    setUserTypeId(userType);
    
    if (appId) {
      getApplicationStage(appId);
    }
  }, []);

  // Check if current user is a sub-user
  const isSubUser = localStorage.getItem('isSubUser') === 'true';
  
  console.log('🔍 Sidebar - isSubUser check:', {
    isSubUser,
    isSubUserValue: localStorage.getItem('isSubUser'),
    userTypeId,
    disciplineId: localStorage.getItem('disciplineId')
  });

  // Check if user should see final submit and dashboard options
  const shouldShowFinalSubmitOptions = ((applicationStage === '18' || applicationStage === '18b') && userTypeId === '4');
  const shouldShowFinaluserSubmitOptions = (applicationStage === '18a');
  
  const MenuItems = [
    { 
      title: 'Submitted Applications', 
      icon: AssignmentIcon, 
      path: '/main/do/dashboard', 
      completed: false,
      id: 'submitted-applications'
    },
    { 
      title: 'MOM Compository', 
      icon: DescriptionIcon, 
      path: '/main/do/momcompository', 
      completed: false,
      id: 'mom-compository'
    },
    { 
      title: 'Group Applications', 
      icon: PeopleIcon, 
      path: '/main/do/doallotment', 
      completed: false,
      id: 'group-applications'
    },
    { 
      title: 'Collection Center', 
      icon: CategoryIcon, 
      path: '/main/do/collection-center', 
      completed: false,
      id: 'collection-center'
    },
    {
      title: 'Laboratory Search',
      icon: SearchIcon,
      path: '/main/laboratory-search',
      completed: false,
      id: 'laboratory-search'
    },
   {
      title: 'Scope Master Search',
      icon: ScienceIcon,
      path: '/main/view-scope-master',
      completed: false,
      id: 'view-scope-master'
    },
   {
      title: 'Equipment Master Search',
      icon: TuneIcon,
      path: '/main/view-equipment-master',
      completed: false,
      id: 'view-equipment-master'
    },
    {
      title: 'Assessor Search',
      icon: PeopleIcon,
      path: '/main/assessor-search',
      completed: false,
      id: 'assessor-search'
    },
    {
      title: 'Accounts Section',
      icon: AccountBalanceWalletIcon,
      path: '/main/do/outstanding-ledger',
      completed: false,
      id: 'outstanding-ledger'
    },
    {
      title: 'Assessment Control',
      icon: PersonPinCircleOutlined,
      path: '/main/do/assessment-control',
      completed: false,
      id: 'assessment-control'
    },
  ];

  

  const handleSectionClick = (item, index) => {
    // Get current lab type from localStorage
    const labTypeId = localStorage.getItem('labTypeId') || '2';
    
    console.log('Sidebar - navigating to:', item.path, 'with labTypeId:', labTypeId);
    
    // Check if it's an external URL
    // Open Laboratory Search in new tab
  if (item.id === 'laboratory-search' || item.id === 'view-scope-master' ||  item.id === 'view-equipment-master') {
    window.open(item.path, '_blank');
  }
  // External URLs
  else if (item.path.startsWith('http://') || item.path.startsWith('https://')) {
      // For external URLs, use window.location.href
      window.location.href = item.path;
    } else {
      // For internal routes, use React Router navigate
      navigate(item.path);
    }
    
    // Close mobile drawer if open
    if (isMobile) {
      onClose();
    }
  };

  return (
    <>
      <Box sx={{ 
        p: 2, 
        borderBottom: '1px solid #e0e0e0',
        transition: 'all 0.3s ease-in-out',
        backgroundColor: isSubUser ? '#e3f2fd' : 'transparent', // Highlight for sub-users
      }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          transition: 'all 0.3s ease-in-out'
        }}>
          {!collapsed ? (
            <Box sx={{
              opacity: collapsed ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
              flexGrow: 1,
            }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                color: '#00024c',
                transition: 'all 0.3s ease-in-out',
              }}>
                Dealing Officer Dashboard
              </Typography>
            </Box>
          ) : (
            <Box sx={{ 
              width: '100%', 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center',
              transition: 'all 0.3s ease-in-out',
            }}>
              {/* <DashboardIcon sx={{ 
                color: '#00024c', 
                fontSize: 28,
                transition: 'all 0.3s ease-in-out',
              }} /> */}
            </Box>
          )}
          
          {/* Collapse/Expand Button for Desktop */}
          {!isMobile && (
            <IconButton
              onClick={onCollapseToggle}
              sx={{
                color: '#fafafaff',
                backgroundColor: '#1959b9ff',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#1959b934',
                  color: '#ffffffff',
                },
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          )}
          
          {/* Close Button for Mobile */}
          {isMobile && (
            <IconButton
              onClick={onClose}
              sx={{ color: '#00024c' }}
            >
              <CloseIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      {/* medical Navigation Items */}
      <Box sx={{ 
        p: collapsed ? 1 : 2, 
        borderBottom: '1px solid #e0e0e0', 
        flexGrow: 1, 
        overflowY: 'auto',
        transition: 'padding 0.3s ease-in-out'
      }}>
        <List sx={{ p: 0 }}>
          {MenuItems.filter(item => {
            // If user is a sub-user, only show Discipline and Proficiency Testing
            if (isSubUser) {
              return item.id === 'discipline' || item.id === 'proficiency-testing';
            }
            
            // For regular users, apply existing conditional logic
            if (!item.conditional) return true;
            if (item.requiresStage18UserType4) return shouldShowFinalSubmitOptions;
            if (item.requiresStage18a) return shouldShowFinaluserSubmitOptions;
            return showDeclaration;
          }).map((item, index) => {
            const isActive = location.pathname === item.path;
            const listItemContent = (
              <ListItemButton
                onClick={() => handleSectionClick(item, index)}
                selected={isActive}
                sx={{
                  borderRadius: 1,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 2,
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                    transform: 'translateX(2px)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                    '&:hover': {
                      backgroundColor: '#bbdefb',
                    },
                  },
                  py: 1,
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: collapsed ? 'unset' : 35,
                  justifyContent: 'center',
                  transition: 'all 0.3s ease-in-out',
                }}>
                  <item.icon sx={{ 
                    fontSize: collapsed ? 24 : 18, 
                    color: item.completed ? '#4caf50' : isActive ? '#1976d2' : '#666',
                    transition: 'all 0.3s ease-in-out',
                  }} />
                </ListItemIcon>
                {!collapsed && (
                  <>
                    <ListItemText
                      primary={item.title}
                      sx={{
                        opacity: collapsed ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                        '& .MuiListItemText-primary': {
                          fontSize: '0.8rem',
                          fontWeight: isActive ? 600 : 500,
                          color: isActive ? '#1976d2' : 'text.primary',
                          transition: 'all 0.3s ease-in-out',
                        },
                      }}
                    />
                    {item.completed && (
                      <VerifiedIcon sx={{ 
                        color: '#4caf50', 
                        fontSize: 16,
                        opacity: collapsed ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                      }} />
                    )}
                  </>
                )}
              </ListItemButton>
            );

            return (
              <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                {collapsed ? (
                  <Tooltip title={item.title} placement="right" arrow>
                    {listItemContent}
                  </Tooltip>
                ) : (
                  listItemContent
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>

    </>
  );
};

export default Sidebar;
